import {
  Route,
  Routes,
  BrowserRouter as Router,
  Navigate,
} from "react-router-dom";
import { lazy, Suspense } from "react";
import "./App.css";
import { captureReferralParam } from "./utils/referralParams";
import { MainLayouts } from "./Layouts/MainLayouts";
import { ConnectModal, TransactionModal } from "./Components/Modal/Modal";
import { WalletSubmitModal } from "./Components/Modal/WalletSubmitModal";
import ReferralEnforcer from "./Components/ReferralEnforcer";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getToastIcon, CloseButton } from "./Components/CustomToast/CustomToast";
import "./Components/CustomToast/CustomToast.css";
import "react-tooltip/dist/react-tooltip.css";
import "react-loading-skeleton/dist/skeleton.css";
// COMMENTED OUT: Markets view - directly opening Los Angeles instead
// import { Markets } from "./Pages/Trading/Markets/MarketIndex";
import { Tooltip } from "react-tooltip";
import { LockDrop } from "./Pages/Lockdrop/Lockdrop";
import { Trading } from "./Pages/Trading/TradingIndex";
// Legacy imports commented out - using RealEstateTrading for all real estate markets
// import { DubaiTrading } from "./Pages/Trading/Markets/RealEstates/Dubai/DubaiTrading";
// import { LosAngelesTrading } from "./Pages/Trading/Markets/RealEstates/LosAngeles/LosAngelesTrading";
// import { NewYorkTrading } from "./Pages/Trading/Markets/RealEstates/NewYork/NewYorkTrading";
// import { ChicagoTrading } from "./Pages/Trading/Markets/RealEstates/Chicago/ChicagoTrading";
// import { WashingtonTrading } from "./Pages/Trading/Markets/RealEstates/Washington/WashingtonTrading";
// import { PittsburghTrading } from "./Pages/Trading/Markets/RealEstates/Pittsburgh/PittsburghTrading";
// import { MiamiBeachTrading } from "./Pages/Trading/Markets/RealEstates/MiamiBeach/MiamiBeachTrading";
// import { BostonTrading } from "./Pages/Trading/Markets/RealEstates/Boston/BostonTrading";
// import { BrooklynTrading } from "./Pages/Trading/Markets/RealEstates/Brooklyn/BrooklynTrading";
// import { AustinTrading } from "./Pages/Trading/Markets/RealEstates/Austin/AustinTrading";
// import { DenverTrading } from "./Pages/Trading/Markets/RealEstates/Denver/DenverTrading";
// Legacy imports commented out - using CryptoTrading for all crypto markets
// import { BtcTrading } from "./Pages/Trading/Markets/Cryptos/BTC/BtcTrading";
// import { AtomTrading } from "./Pages/Trading/Markets/Cryptos/Atom/AtomTrading";
// import { EthTrading } from "./Pages/Trading/Markets/Cryptos/Eth/EthTrading";
import CryptoTrading from "./Pages/Trading/Markets/components/trading/CryptoTrading";
import RealEstateTrading from "./Pages/Trading/Markets/components/trading/RealEstateTrading";
import WatchTrading from "./Pages/Trading/Markets/components/trading/WatchTrading";
import CardTrading from "./Pages/Trading/Markets/components/trading/CardTrading";
import AllTradesPage from "./Pages/Trading/Trades/AllTradesPage";
import ShareTradePage from "./Pages/Trading/ShareTradePage";

import VaultsIndex from "./Pages/Trading/Vaults/VaultsIndex";
// import VaultsList from "./Pages/Trading/Vaults/VaultsList";
import VaultDetail from "./Pages/Trading/Vaults/VaultDetail";
import MyDeposits from "./Pages/Trading/Vaults/MyDeposits";
import VaultsTopBar from "./Components/VaultsComponents/VaultsTopBar";
import Terms from "./Pages/Legal/Terms";
import TermsModal from "./Components/Modal/TermsModal";

// Capture ?ref= URL parameter IMMEDIATELY before React renders.
// Must run at module level — useEffect is too late because <Navigate replace>
// strips query params during the first render, before any effect fires.
captureReferralParam();

// Lazy load standalone pages to avoid increasing main bundle size
const ClaimPage = lazy(() => import("./Pages/Referral/ClaimPage"));
const ReferralAdmin = lazy(() => import("./Pages/Admin/ReferralAdmin"));
const Leaderboard = lazy(() => import("./Pages/Leaderboard/Leaderboard"));
const PointsAdmin = lazy(() => import("./Pages/Admin/PointsAdmin"));

// Redirect component for rental routes (redirects to main app)
const RentRedirect = () => {
  window.location.href = 'https://app.codedestate.com';
  return null;
};

function App() {

  return (
    <div
      // `--bg` is the page background behind everything (ADDENDUM-8 §1), and it
      // resolves to the old #fafafa in light mode. The `bg-[#fafafa]` class it
      // replaces has to go rather than stay as a fallback: the generated sweep
      // keys off that class name and would repaint the root as a card surface.
      style={{ fontFamily: "General Sans", background: "var(--bg)" }}
      className="ce-app-root font-medium"
    >
      <Router>
        {/* Enforce ESTATE referral for every connected wallet, all routes/paths */}
        <ReferralEnforcer />
        <Routes>
          {/* Referral claim page (standalone, no header) */}
          <Route path="/claim" element={<Suspense fallback={<div className="min-h-screen bg-[#fafafa]" />}><ClaimPage /></Suspense>} />

          {/* Admin referral dashboard (standalone, no header) */}
          <Route path="/admin/referral" element={<Suspense fallback={<div className="min-h-screen bg-[#fafafa]" />}><ReferralAdmin /></Suspense>} />

          {/* Admin points dashboard (standalone, no header) */}
          <Route path="/admin/points" element={<Suspense fallback={<div className="min-h-screen bg-[#fafafa]" />}><PointsAdmin /></Suspense>} />

          {/* Lockdrop route outside MainLayouts (no header) */}
          <Route path="/earn" element={<LockDrop />} />
          
          {/* Share trade route outside MainLayouts (no header) */}
          <Route path="/share/trade/:encoded" element={<ShareTradePage />} />

          {/* Terms of Use page (standalone, no header) */}
          <Route path="/terms" element={<Terms />} />
          
          {/* Redirect rental routes to main app */}
          <Route path="/rent/*" element={<RentRedirect />} />
          
          {/* Main app routes */}
          <Route path="/" element={<MainLayouts />}>
            {/* Redirect root directly to Los Angeles market (skip MarketIndex) */}
            <Route index element={<Navigate to="/perps/markets/realestate/losangeles" replace />} />
            
            {/* Perps/Trading routes */}
            <Route path="perps/*" element={<Trading />}>
              {/* COMMENTED OUT: Markets view routes - directly opening Los Angeles instead */}
              {/* <Route path="market" element={<Markets />} /> */}
              {/* <Route path="markets/*" element={<Markets />}></Route> */}
              <Route path="trades" element={<AllTradesPage />} />
              <Route path="leaderboard" element={<Suspense fallback={<div className="min-h-screen bg-[#F6F6F6]" />}><Leaderboard /></Suspense>} />
              
              {/* Redirect routes to Los Angeles market - COMMENTED OUT Markets view */}
              {/* Redirect /perps/markets/crypto to Los Angeles */}
              <Route path="markets/crypto" element={<Navigate to="/perps/markets/realestate/losangeles" replace />} />
              {/* Redirect /perps/markets to Los Angeles */}
              <Route path="markets" element={<Navigate to="/perps/markets/realestate/losangeles" replace />} />
              
              {/* Real Estate Markets - Dynamic route for all real estate markets */}
              <Route path="markets/realestate/:marketId" element={<RealEstateTrading />} />
              
              {/* Crypto Markets - Dynamic route for all crypto markets */}
              <Route path="markets/crypto/:marketId" element={<CryptoTrading />} />
              
              {/* Collectible card markets — the 3D trade cockpit.
                  Static paths outrank the dynamic ones below, so only these two
                  markets get the cockpit; every other RWA/PoC market is
                  unchanged. */}
              <Route path="markets/rwa/poke" element={<CardTrading marketId="poke" />} />
              <Route path="markets/rwa/psa-10" element={<CardTrading marketId="psa-10" />} />

              {/* PSA-10 went live on 2026-08-10 and moved out of /poc. Keep the
                  old path working so shared links and bookmarks still land. */}
              <Route path="markets/poc/psa-10" element={<Navigate to="/perps/markets/rwa/psa-10" replace />} />

              {/* RWA Markets - Dynamic route for all RWA markets */}
              <Route path="markets/rwa/:marketId" element={<CryptoTrading />} />

              {/* Watch Markets - Dynamic route for all watch RWA markets */}
              <Route path="markets/watch/:marketId" element={<WatchTrading />} />
              
              {/* Stock Markets - Dynamic route for all stock markets */}
              <Route path="markets/stocks/:marketId" element={<CryptoTrading />} />

              {/* Commodity Markets - Dynamic route for all commodity markets */}
              <Route path="markets/commodities/:marketId" element={<CryptoTrading />} />

              {/* PoC Markets - Preview mode for new markets without contracts */}
              <Route path="markets/poc/:marketId" element={<CryptoTrading />} />

              {/* Legacy routes for backward compatibility (will be handled by dynamic route above) */}
              {/* <Route path="markets/crypto/btc" element={<BtcTrading />} /> */}
              {/* <Route path="markets/crypto/eth" element={<EthTrading />} /> */}
              {/* <Route path="markets/crypto/atom" element={<AtomTrading />} /> */}
              
              {/* Catch-all for any unmatched perps routes - redirect to Los Angeles */}
              {/* Redirect /perps/markets/* (any unmatched markets path) to Los Angeles */}
              <Route path="markets/*" element={<Navigate to="/perps/markets/realestate/losangeles" replace />} />
              {/* Redirect /perps/* (any unmatched perps path) to Los Angeles */}
              <Route path="*" element={<Navigate to="/perps/markets/realestate/losangeles" replace />} />

            {/* Vault routes */}
            <Route path="vaults" element={<VaultsIndex />}>
              {/* Redirect /perps/vaults to /perps/vaults/usdc (single vault for now) */}
              <Route path="" element={<Navigate to="/perps/vaults/usdc" replace />} />
              {/* <Route path="" element={<VaultsList />} /> */}
              <Route path=":vaultId" element={<VaultDetail />} />
            </Route>
            <Route 
              path="deposits" 
              element={
                <div className="w-full px-4 pt-4" style={{ fontFamily: "General Sans" }}>
                  <VaultsTopBar />
                  <MyDeposits />
                </div>
              } 
            />
            </Route>
            
            {/* Catch-all route for invalid URLs - redirect to Los Angeles */}
            <Route path="*" element={<Navigate to="/perps/markets/realestate/losangeles" replace />} />
          </Route>
        </Routes>
        
        {/* Global Modals */}
        <TransactionModal />
        <ConnectModal />
        <WalletSubmitModal />
        <TermsModal />
      </Router>
      
      {/* Toast Notifications */}
      <ToastContainer
        position="bottom-right"
        autoClose={3000}
        newestOnTop={false}
        closeOnClick={true}
        rtl={false}
        pauseOnFocusLoss
        draggable
        theme="light"
        className="custom-toast-container"
        icon={getToastIcon}
        closeButton={({ closeToast, type }) => <CloseButton closeToast={closeToast} type={type} />}
      />
      
      {/* Tooltips */}
      <Tooltip
        id="my-tooltip"
        content="Coming soon"
        style={{ 
          borderRadius: "4px", 
          padding: "6px", 
          zIndex: 999999999,
          backgroundColor: "#333",
          color: "white"
        }}
      />
      <Tooltip
        id="pnl-tooltip"
        style={{ 
          borderRadius: "4px", 
          padding: "8px 12px", 
          zIndex: 999999999,
          backgroundColor: "#333",
          color: "white",
          maxWidth: "300px",
          fontSize: "12px",
          lineHeight: "1.5",
          whiteSpace: "pre-line"
        }}
      />
    </div>
  );
}

export default App;